import { IMPORT_STAGES } from '@palaccount/contracts';
import type {
  BillResponse,
  CreditNoteResponse,
  DeliveryNoteResponse,
  GoodsReceiptResponse,
  ImportOrderResponse,
  OrderResponse,
  PurchaseOrderResponse,
  PurchaseReturnResponse,
  QuotationResponse,
} from '@palaccount/contracts';
import type {
  CreditNoteData,
  DeliveryNoteData,
  GoodsReceiptData,
  ImportOrderData,
  PricedLineData,
  PurchaseInvoiceData,
  PurchaseOrderData,
  PurchaseReturnData,
  QuotationData,
  SalesOrderData,
} from '@palaccount/documents';

/**
 * Wave 2 of `specs/erp-gap-analysis.md` — the browser's copy of each record,
 * for the INSTANT local print.
 *
 * `invoiceDocument` in `sales/invoice-editor-page.tsx` is the exemplar and its
 * header is the rule: a straight field mapping from the contract response to
 * the template's data, with NO arithmetic over money — every figure is the
 * stored PART 18 block exactly as the screen shows it. The PDF the worker
 * produces does NOT come through here; `document-data.ts` re-reads the row
 * under RLS. Both feed the same template (`packages/documents`), which is the
 * property DECISION.md §2.13 bought.
 *
 * ── Units on the priced documents ─────────────────────────────────────────
 * Every priced line response now carries the N-4 pair (`unitSymbol`/
 * `unitPrecision`): wave 4C mirrored the invoice's onto the quotation, sales
 * order, purchase order and bill lines, which until then printed `unit: null`
 * here while the worker's PDF printed the unit. The paper and the PDF now
 * read the same unit mark beside a quantity.
 */

/** A line from any priced response, with its N-4 unit pair. */
type PricedResponseLine = {
  lineNo: number;
  itemCode: string | null;
  description: string;
  qty: string;
  unitSymbol: string | null;
  unitPrecision: number | null;
  unitPrice: string;
  discountPct: string;
  lineTotal: string;
};

function pricedLine(line: PricedResponseLine): PricedLineData {
  return {
    lineNo: line.lineNo,
    itemCode: line.itemCode,
    description: line.description,
    qty: line.qty,
    unit: unitOf(line),
    unitPrice: line.unitPrice,
    discountPct: line.discountPct,
    lineTotal: line.lineTotal,
  };
}

/** The N-4 pair as the credit note, return, delivery note and GRN responses carry it. */
function unitOf(line: {
  unitSymbol: string | null;
  unitPrecision: number | null;
}): { symbol: string; precision: number } | null {
  return line.unitSymbol === null || line.unitPrecision === null
    ? null
    : { symbol: line.unitSymbol, precision: line.unitPrecision };
}

/**
 * `a − b` on two quantity strings, exact to three decimals.
 *
 * NOT `Number(a) - Number(b)`: `10.1 - 0.3` is `9.799999…` in a float, and the
 * loader computes the same figure in SQL as `numeric(18,3)`. Integer thousandths
 * give the same answer the database gives, which is the only answer the paper
 * may carry.
 */
function subtractQty(a: string, b: string): string {
  const thousandths = (value: string): bigint => {
    const match = /^(-?)(\d+)(?:\.(\d{0,3})\d*)?$/.exec(value.trim());
    if (!match) return 0n;
    const sign = match[1] ?? '';
    const whole = match[2] ?? '0';
    const fraction = match[3] ?? '';
    const scaled = BigInt(whole) * 1000n + BigInt(fraction.padEnd(3, '0'));
    return sign === '-' ? -scaled : scaled;
  };
  const result = thousandths(a) - thousandths(b);
  const negative = result < 0n;
  const magnitude = negative ? -result : result;
  const whole = magnitude / 1000n;
  const fraction = (magnitude % 1000n).toString().padStart(3, '0');
  return `${negative ? '-' : ''}${whole}.${fraction}`;
}

export function quotationDocument(quotation: QuotationResponse): QuotationData {
  return {
    code: quotation.code,
    revisionNo: quotation.revisionNo,
    status: quotation.status,
    quotationDate: quotation.quotationDate,
    validUntil: quotation.validUntil,
    partnerCode: quotation.partnerCode,
    partnerName: quotation.partnerName,
    reference: quotation.reference,
    terms: quotation.terms,
    attention: quotation.attention,
    currency: quotation.currency,
    lines: quotation.lines.map(pricedLine),
    subtotal: quotation.subtotal,
    discountPct: quotation.discountPct,
    discountAmount: quotation.discountAmount,
    taxPct: quotation.taxPct,
    taxAmount: quotation.taxAmount,
    grandTotal: quotation.grandTotal,
    notes: quotation.notes,
  };
}

export function salesOrderDocument(order: OrderResponse): SalesOrderData {
  return {
    code: order.code,
    status: order.status,
    orderDate: order.orderDate,
    expectedDate: order.expectedDate,
    quotationCode: order.sourceQuotationCode,
    partnerCode: order.partnerCode,
    partnerName: order.partnerName,
    currency: order.currency,
    lines: order.lines.map(pricedLine),
    subtotal: order.subtotal,
    discountPct: order.discountPct,
    discountAmount: order.discountAmount,
    taxPct: order.taxPct,
    taxAmount: order.taxAmount,
    grandTotal: order.grandTotal,
    notes: order.notes,
  };
}

/**
 * SAL-03. `remaining` on the response is ordered less every EARLIER posted
 * note; the paper's "remaining" is what the order is still owed AFTER this
 * note, so this note's own quantity comes off too — unless it was cancelled,
 * exactly as `deliveryNoteData` counts it.
 */
export function deliveryNoteDocument(delivery: DeliveryNoteResponse): DeliveryNoteData {
  const counted = delivery.status !== 'cancelled';
  return {
    code: delivery.code,
    status: delivery.status,
    deliveryDate: delivery.deliveryDate,
    salesOrderCode: delivery.salesOrderCode,
    partnerCode: delivery.partnerCode,
    partnerName: delivery.partnerName,
    warehouseName: delivery.warehouseName,
    lines: delivery.lines.map((line) => ({
      lineNo: line.lineNo,
      itemCode: line.itemCode,
      description: line.description,
      unit: unitOf(line),
      qtyOrdered: line.qtyOrdered,
      qtyDelivered: line.qty,
      qtyRemaining: counted ? subtractQty(line.remaining, line.qty) : line.remaining,
      batchNo: line.batchNo,
      serialNos: line.serialNos ?? [],
      note: line.note,
      // Wave 5B: a line not on the order prints flagged, with its reason.
      isExtra: line.isExtra,
      extraReason: line.extraReason,
    })),
    driver: delivery.driver,
    vehicleNo: delivery.vehicleNo,
    vehicleType: delivery.vehicleType,
    departedAt: delivery.departedAt,
    deliveryAddress: delivery.deliveryAddress,
    receivedBy: delivery.receivedBy,
    receivedAt: delivery.receivedAt,
    receiverNote: delivery.receiverNote,
    notes: delivery.notes,
  };
}

/**
 * SAL-05. Credit-note lines carry `itemId` and no code (the grid shows the
 * description); the worker's loader joins `items`. The screen passes whatever
 * catalogue it already holds — the invoice-backed editor's `useItemsHeld` —
 * and a line whose code it does not know prints without one, as a free-text
 * line would.
 */
export function creditNoteDocument(
  creditNote: CreditNoteResponse,
  itemCodeOf: (itemId: string) => string | null = () => null,
): CreditNoteData {
  return {
    code: creditNote.code,
    status: creditNote.status,
    creditDate: creditNote.creditDate,
    invoiceCode: creditNote.invoiceCode,
    reason: creditNote.reason,
    reasonNote: creditNote.reasonNote,
    restock: creditNote.restock,
    partnerCode: creditNote.partnerCode,
    partnerName: creditNote.partnerName,
    currency: creditNote.currency,
    lines: creditNote.lines.map((line) =>
      pricedLine({ ...line, itemCode: line.itemId === null ? null : itemCodeOf(line.itemId) }),
    ),
    subtotal: creditNote.subtotal,
    discountPct: creditNote.discountPct,
    discountAmount: creditNote.discountAmount,
    taxPct: creditNote.taxPct,
    taxAmount: creditNote.taxAmount,
    grandTotal: creditNote.grandTotal,
    notes: creditNote.notes,
  };
}

export function purchaseOrderDocument(order: PurchaseOrderResponse): PurchaseOrderData {
  return {
    code: order.code,
    status: order.status,
    orderDate: order.orderDate,
    expectedDate: order.expectedDate,
    sentAt: order.sentAt,
    partnerCode: order.partnerCode,
    partnerName: order.partnerName,
    currency: order.currency,
    lines: order.lines.map(pricedLine),
    subtotal: order.subtotal,
    discountPct: order.discountPct,
    discountAmount: order.discountAmount,
    taxPct: order.taxPct,
    taxAmount: order.taxAmount,
    grandTotal: order.grandTotal,
    notes: order.notes,
  };
}

/**
 * Wave 5E — PUR-06's shipment for the instant local print: the server's
 * recomputed figures as the screen holds them, never re-derived here. The
 * partner CODE is not on the shipment response, so the browser copy prints
 * the name alone; the worker's PDF resolves the code under RLS.
 */
export function importOrderDocument(
  order: ImportOrderResponse,
  baseCurrency: string,
): ImportOrderData {
  return {
    code: order.ref,
    stage: order.stage,
    stages: order.stage === 'cancelled' ? [...IMPORT_STAGES, 'cancelled'] : [...IMPORT_STAGES],
    partnerCode: '',
    partnerName: order.supplierName,
    purchaseOrderCode: order.purchaseOrderCode,
    manualRef: order.manualRef,
    orderDate: order.orderDate,
    shipDate: order.shipDate,
    arriveDate: order.arriveDate,
    incoterm: order.incoterm,
    shipMethod: order.shipMethod,
    carrier: order.carrier,
    trackingNo: order.trackingNo,
    warehouseName: order.warehouseName,
    currency: order.currencyCode,
    rate: order.rate,
    baseCurrency,
    allocMethod: order.allocMethod,
    lines: order.lines.map((line) => ({
      lineNo: line.lineNo,
      itemCode: line.sku,
      description: line.name,
      hsCode: line.hsCode,
      originCountry: line.originCountry,
      qty: line.qty,
      unit: line.unitSymbol === '' ? null : { symbol: line.unitSymbol, precision: line.unitPrecision },
      unitPrice: line.unitPrice,
      lineCost: line.lineCost,
      chargeShare: line.chargeShare,
      landedUnitCost: line.landedUnitCost,
      landedUnitCostBase: line.landedUnitCostBase,
    })),
    charges: order.charges.map((charge) => ({
      category: charge.category,
      label: charge.label,
      amount: charge.amount,
      currency: charge.currency,
      amountOrder: charge.amountOrder,
      billCode: charge.purchaseInvoiceCode,
    })),
    itemsCost: order.summary.itemsCost,
    chargesTotal: order.summary.chargesTotal,
    totalLanded: order.summary.totalLanded,
    totalLandedBase: order.summary.totalLandedBase,
    notes: order.notes,
    cancelReason: order.cancelReason,
  };
}

export function purchaseInvoiceDocument(bill: BillResponse): PurchaseInvoiceData {
  return {
    code: bill.code,
    status: bill.status,
    invoiceDate: bill.invoiceDate,
    dueDate: bill.dueDate,
    supplierInvoiceNo: bill.supplierInvoiceNo,
    purchaseOrderCode: bill.purchaseOrderCode,
    partnerCode: bill.partnerCode,
    partnerName: bill.partnerName,
    currency: bill.currency,
    lines: bill.lines.map(pricedLine),
    subtotal: bill.subtotal,
    discountPct: bill.discountPct,
    discountAmount: bill.discountAmount,
    taxPct: bill.taxPct,
    taxAmount: bill.taxAmount,
    grandTotal: bill.grandTotal,
    notes: bill.notes,
  };
}

/** PUR-02. `remainingQty` is ordered less earlier receipts; this one's accepted figure comes off too. */
export function goodsReceiptDocument(receipt: GoodsReceiptResponse): GoodsReceiptData {
  const counted = receipt.status !== 'cancelled';
  return {
    code: receipt.code,
    status: receipt.status,
    receiptDate: receipt.receiptDate,
    purchaseOrderCode: receipt.purchaseOrderCode,
    supplierRef: receipt.supplierRef,
    // Wave 5C — the lorry's paper and the people who received and checked it.
    supplierDeliveryNoteNo: receipt.supplierDeliveryNoteNo,
    receivedByName: receipt.receivedByName,
    checkedByName: receipt.checkedByName,
    driver: receipt.driver,
    vehicleNo: receipt.vehicleNo,
    manualRef: receipt.manualRef,
    bookNo: receipt.bookNo,
    partnerCode: receipt.partnerCode,
    partnerName: receipt.partnerName,
    warehouseName: receipt.warehouseName,
    lines: receipt.lines.map((line) => ({
      lineNo: line.lineNo,
      itemCode: line.itemCode,
      description: line.description,
      unit: unitOf(line),
      qtyOrdered: line.qtyOrdered,
      qtyReceived: line.qtyReceived,
      qtyAccepted: line.acceptedQty,
      qtyRemaining: counted ? subtractQty(line.remainingQty, line.acceptedQty) : line.remainingQty,
      quality: line.quality,
      batchNo: line.batchNo,
      serialNos: line.serialNos ?? [],
      note: line.note,
    })),
    notes: receipt.notes,
  };
}

export function purchaseReturnDocument(record: PurchaseReturnResponse): PurchaseReturnData {
  return {
    code: record.code,
    status: record.status,
    returnDate: record.returnDate,
    billCode: record.billCode,
    reason: record.reason,
    reasonNote: record.reasonNote,
    restocked: record.restocked,
    valueOnly: record.valueOnly,
    partnerCode: record.partnerCode,
    partnerName: record.partnerName,
    currency: record.currency,
    lines: record.lines.map(pricedLine),
    subtotal: record.subtotal,
    discountPct: record.discountPct,
    discountAmount: record.discountAmount,
    taxPct: record.taxPct,
    taxAmount: record.taxAmount,
    grandTotal: record.grandTotal,
    notes: record.notes,
  };
}
